"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VarianceChecker = void 0;
const CommonParameterUnits_1 = require("../CommonParameterUnits");
const SIValueTool_1 = require("./SIValueTool");
class VarianceChecker {
    static { this.plusMinusSign = '+/-'; }
    static { this.ValueVarianceEpsilon = { variance: this.plusMinusSign + '0.00001%' }; }
    static { this.VarianceZeroValueEpsilon = 1e-7; }
    static compareAbsoluteNumberWithVariance(value, expected, variance) {
        const upperResult = VarianceChecker.compareMaxNumberWithVariance(value, expected, variance, true);
        const lowerResult = VarianceChecker.compareMinNumberWithVariance(value, expected, variance, true);
        return upperResult && lowerResult;
    }
    static compareMaxNumberWithVariance(value, expected, variance, valInclusive) {
        const comparison = valInclusive ?
            (value, assert) => value <= assert
            : (value, assert) => value < assert;
        const assertValue = VarianceChecker.getMaxValueWithVariance(expected, variance);
        return comparison(value, assertValue);
    }
    static compareMinNumberWithVariance(value, expected, variance, valInclusive) {
        const comparison = valInclusive ?
            (value, assert) => value >= assert
            : (value, assert) => value > assert;
        const assertValue = VarianceChecker.getMinValueWithVariance(expected, variance);
        return comparison(value, assertValue);
    }
    static getMaxValueWithVariance(value, variance, units) {
        let varianceProps;
        if (typeof variance === 'string') {
            if (!units) {
                throw new Error('Units must be provided when using string variance');
            }
            varianceProps = VarianceChecker.valueVarianceToProperties({ variance }, units);
        }
        else {
            varianceProps = variance;
        }
        const varianceOperand = VarianceChecker.getVarianceOperand(value, varianceProps);
        if (varianceProps?.singleSided) {
            return varianceOperand > 0 ? value + varianceOperand : value;
        }
        const max = value + varianceOperand;
        return max;
    }
    static getMinValueWithVariance(value, variance, units) {
        let varianceProps;
        if (typeof variance === 'string') {
            if (!units) {
                throw new Error('Units must be provided when using string variance');
            }
            varianceProps = VarianceChecker.valueVarianceToProperties({ variance }, units);
        }
        else {
            varianceProps = variance;
        }
        const varianceOperand = VarianceChecker.getVarianceOperand(value, varianceProps);
        if (varianceProps?.singleSided) {
            return varianceOperand < 0 ? value + varianceOperand : value;
        }
        const min = value - varianceOperand;
        return min;
    }
    static buildValueVariance(value, units, singleSided = false) {
        let varianceString = '';
        if (!singleSided) {
            value = Math.abs(value);
            varianceString += VarianceChecker.plusMinusSign;
        }
        else if (value > 0) {
            varianceString += '+';
        }
        const valueStr = this.formatEngNotation(value);
        varianceString += `${valueStr}${units.short}`;
        return { variance: varianceString };
    }
    static valueVarianceToProperties(variance, baseUnits) {
        const sanitizedValue = variance.variance?.replace(/\s/g, '') ?? '';
        if (!sanitizedValue) {
            return undefined;
        }
        const varianceRegex = /^((±|\+\/-|-\/\+)|[+-])?([0-9]*\.?[0-9]+(?:e[-+]?[0-9]+)?)(\D+)$/i;
        const match = varianceRegex.exec(sanitizedValue);
        if (!match) {
            return undefined;
        }
        const isSingleSided = match[1] !== undefined && match[2] === undefined;
        const valueStr = isSingleSided ? match[1] + match[3] : match[3];
        const value = parseFloat(valueStr);
        const valueAndUnits = value.toString() + match[4];
        const vProps = VarianceChecker.parseSiVariance(valueAndUnits, baseUnits);
        vProps.singleSided = isSingleSided;
        return vProps;
    }
    static sanitizeValueVariance(variance, units) {
        const varianceProps = VarianceChecker.valueVarianceToProperties(variance, units);
        variance.variance = VarianceChecker.buildValueVariance(varianceProps.value, varianceProps.units, varianceProps.singleSided).variance;
        return variance;
    }
    static parseSiVariance(value, units) {
        let newValue;
        let vProps;
        try {
            newValue = (0, SIValueTool_1.parseNumericalSIValue)(value, CommonParameterUnits_1.CommonParameterUnits.percentage);
            vProps = {
                value: newValue,
                units: CommonParameterUnits_1.CommonParameterUnits.percentage,
                singleSided: undefined,
            };
        }
        catch {
            newValue = (0, SIValueTool_1.parseNumericalSIValue)(value, units);
            vProps = {
                value: newValue,
                units: units,
                singleSided: undefined,
            };
        }
        return vProps;
    }
    static formatEngNotation(num) {
        let exp = Math.floor(Math.log10(Math.abs(num)));
        exp = Math.round(exp / 3) * 3;
        let base = num / Math.pow(10, exp);
        if (Math.abs(base) < 1) {
            base = base * Math.pow(10, 3);
            exp -= 3;
        }
        let returnStr = base.toFixed(3);
        returnStr = returnStr.replace(/\.?0*$/, '');
        if (exp != 0) {
            returnStr += 'e' + exp;
        }
        return returnStr;
    }
    static getVarianceOperand(value, variance) {
        if (variance === undefined) {
            return 0;
        }
        const isPercent = variance.units ? variance.units.short === CommonParameterUnits_1.CommonParameterUnits.percentage.short : false;
        if (isPercent && Math.abs(value) < 10 * Number.EPSILON) {
            return variance.value < 0 ? -VarianceChecker.VarianceZeroValueEpsilon : VarianceChecker.VarianceZeroValueEpsilon;
        }
        const operand = isPercent ? (Math.abs(value) * variance.value) / 100 : variance.value;
        return operand;
    }
    static firstDefinedValue(...values) {
        for (const value of values) {
            if (value === undefined && value === null) {
                continue;
            }
            if (typeof value === 'object') {
                if (value?.value !== undefined && value?.value !== null) {
                    return value.value;
                }
                continue;
            }
            return value;
        }
        return undefined;
    }
}
exports.VarianceChecker = VarianceChecker;
//# sourceMappingURL=variance-checker.js.map